import { Check, Minus } from "lucide-react";
import { PageHeader } from "../components/app/PageHeader";

const ROLES = ["Parish Priest / Super Admin", "Parish Administrator", "PPC / PEC Officer", "Ministry Coordinator", "GKK President", "Parishioner"];
const MATRIX = [
  ["View aggregate parish findings", [1, 1, 1, 1, 0, 0]],
  ["View aggregate findings for own GKK", [1, 1, 1, 1, 1, 0]],
  ["View public parish summary", [1, 1, 1, 1, 1, 1]],
  ["Import survey files", [1, 1, 0, 0, 0, 0]],
  ["Manage GKK & Unit list", [1, 1, 0, 0, 0, 0]],
  ["Review feedback", [1, 1, 0, 0, 0, 0]],
  ["Submit feedback & suggestions", [1, 1, 1, 1, 1, 1]],
  ["Propose priorities & action plans", [1, 1, 1, 1, 1, 0]],
  ["Approve priorities, plans & budgets; defer / cancel", [1, 0, 0, 0, 0, 0]],
  ["Send plans for review, evaluate completed plans", [1, 1, 0, 0, 0, 0]],
  ["Record monitoring (or the assigned coordinator)", [1, 1, 0, 0, 0, 0]],
  ["See all action plans", [1, 1, 1, 0, 0, 0]],
  ["See own / assigned / own-GKK plans", [1, 1, 1, 1, 1, 0]],
  ["Mission evaluation (scoped to visible plans)", [1, 1, 1, 1, 1, 0]],
  ["Parish reports (PDF / Excel)", [1, 1, 1, 0, 0, 0]],
  ["GKK review report for own GKK", [1, 1, 1, 0, 1, 0]],
  ["Pastoral Briefing for the Parish Priest", [1, 1, 0, 0, 0, 0]],
  ["Diocesan report", [1, 0, 0, 0, 0, 0]],
  ["Rate programs (anonymous, voluntary)", [1, 1, 1, 1, 1, 1]],
  ["AI Pastoral Analyst", [1, 1, 0, 0, 0, 0]],
  ["Create program feedback QR (or assigned coordinator)", [1, 1, 0, 0, 0, 0]],
  ["Manage users & roles", [1, 0, 0, 0, 0, 0]],
  ["View audit trail, backups, sample data", [1, 0, 0, 0, 0, 0]],
  ["View identifiable household records", [0, 0, 0, 0, 0, 0]],
];

const SAFEGUARDS = [
  "Data minimization: names, addresses and contact details are never stored by this system. Each household is kept only as a one-way coded reference for de-duplication.",
  "Consent: survey rows without recorded consent are rejected at import.",
  "Small-group suppression: any count from 1 to 4 is hidden; results for fewer than 5 households are not shown.",
  "Missing answers are reported separately and never counted as zero.",
  "No ranking of GKKs by poverty, religious practice or vulnerability.",
  "Confession, private spiritual counsel and safeguarding disclosures are never recorded in this system.",
  "Encryption in transit (HTTPS) and secure, HTTP-only session cookies; sessions expire after 7 days; deactivated accounts are signed out immediately.",
  "Every sign-in, import, role change and refused access attempt is written to the audit trail.",
  "Sign-in uses Google accounts; privileged users should enable Google 2-Step Verification.",
  "Backups are downloaded by the Parish Priest only and must be stored securely. Retention and deletion schedules are to be set by the parish (Phase 3).",
];

export default function Privacy() {
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Data Privacy Act of 2012 (RA 10173) & Church safeguarding" title="Privacy & Permissions" testId="privacy-header">
        This system serves pastoral accompaniment. It never decides eligibility for assistance, never labels families, and never replaces pastoral judgment.
      </PageHeader>
      <div className="overflow-x-auto rounded-xl border border-amber-700/15 bg-white p-5" data-testid="permission-matrix">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-stone-500"><th className="py-2 pr-4">Permission</th>{ROLES.map((r) => <th key={r} className="px-2 text-xs">{r}</th>)}</tr></thead>
          <tbody>{MATRIX.map(([p, row]) => (
            <tr key={p} className="border-t border-amber-700/10"><td className="py-2 pr-4 text-stone-800">{p}</td>
              {row.map((v, i) => <td key={i} className="px-2">{v ? <Check className="h-4 w-4 text-[#285E3B]" /> : <Minus className="h-4 w-4 text-stone-300" />}</td>)}</tr>))}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-stone-500">GKK Presidents see only the GKK assigned to them by the Parish Priest.</p>
      </div>
      <ul className="grid gap-3 md:grid-cols-2" data-testid="safeguards-list">
        {SAFEGUARDS.map((s) => <li key={s} className="rounded-xl border border-amber-700/15 bg-[#F5F0E6] p-4 text-sm text-stone-700">{s}</li>)}
      </ul>
    </div>
  );
}
